import { useState, useEffect, useMemo } from "react";
import Api from "../../Auth/Api";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { getColorsForLabels } from '../../utils/chartColors';
import { customStyles } from '../../utils/selectStyles';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { FeedbackSkeleton } from '../../Components/Skeletons/FeedbackSkeleton';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function RelatorioFeedback() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewType, setViewType] = useState('pizza'); // pizza, table, bar
    const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
    const [anosDisponiveis, setAnosDisponiveis] = useState([]);
    const [anosComparacao, setAnosComparacao] = useState([]);
    const [modoComparacao, setModoComparacao] = useState(false);
    const [showModalComparacao, setShowModalComparacao] = useState(false);
    const [anosSelecionadosComparacao, setAnosSelecionadosComparacao] = useState([]);

    const anosOptions = useMemo(() => {
        return anosDisponiveis.map(ano => ({
            value: ano,
            label: ano.toString()
        }));
    }, [anosDisponiveis]);

    useEffect(() => {
        const fetchAnos = async () => {
            try {
                const response = await Api.get('api/anos-pesquisa-satisfacao', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const anos = response.data;
                setAnosDisponiveis(anos);
                if (anos.length > 0 && !anos.includes(anoSelecionado)) {
                    setAnoSelecionado(anos[0]);
                }
            } catch (error) {
                console.error("Erro ao carregar anos disponíveis:", error);
            }
        };
        fetchAnos();
    }, [token, anoSelecionado]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let url = 'api/estatisticas-pesquisa-satisfacao';
                if (modoComparacao && anosComparacao.length > 0) {
                    const anosParam = anosComparacao.map(ano => `anos[]=${ano}`).join('&');
                    url += `?${anosParam}`;
                } else {
                    url += `?ano=${anoSelecionado}`;
                }
                
                const response = await Api.get(url, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setData(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Erro ao carregar estatísticas de feedback:", error);
                setLoading(false);
            }
        };
        fetchData();
    }, [token, anoSelecionado, modoComparacao, anosComparacao]);

    const createChartData = (question, responses) => {
        const labels = Object.keys(responses);
        const values = Object.values(responses);
        const backgroundColor = getColorsForLabels(labels);

        return {
            labels,
            datasets: [{
                data: values,
                backgroundColor,
                borderWidth: 1
            }]
        };
    };

    const createBarChartData = (question, responses) => {
        const labels = Object.keys(responses);
        const values = Object.values(responses);
        const backgroundColor = getColorsForLabels(labels);

        return {
            labels,
            datasets: [{
                label: 'Respostas',
                data: values,
                backgroundColor,
                borderWidth: 1
            }]
        };
    };

    const renderVisualization = (question, responses, index) => {
        switch (viewType) {
            case 'pizza':
                return (
                    <div style={{ height: '300px' }}>
                        <Pie
                            data={createChartData(question, responses)}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'bottom' }
                                }
                            }}
                        />
                    </div>
                );
            case 'table':
                return (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Resposta</th>
                                    <th>Quantidade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(responses).map(([response, count], i) => (
                                    <tr key={i}>
                                        <td>{response}</td>
                                        <td>{count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            case 'bar':
                return (
                    <div style={{ height: '300px' }}>
                        <Bar
                            data={createBarChartData(question, responses)}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            stepSize: 1
                                        }
                                    }
                                }
                            }}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    const handleExportExcel = () => {
        if (!data) return;

        const workbook = XLSX.utils.book_new();

        if (modoComparacao && typeof data === 'object' && !Array.isArray(data)) {
            // Modo comparação: criar uma aba por ano
            Object.entries(data).forEach(([ano, estatisticasAno]) => {
                const dataForExcel = Object.entries(estatisticasAno).map(([question, responses]) => {
                    const row = {
                        'Pergunta': question,
                    };
                    
                    Object.entries(responses).forEach(([response, count]) => {
                        row[response] = count;
                    });
                    
                    return row;
                });
                
                const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
                XLSX.utils.book_append_sheet(workbook, worksheet, `Feedback ${ano}`);
            });
        } else {
            // Modo normal: uma única aba
            const dataForExcel = Object.entries(data).map(([question, responses]) => {
                const row = {
                    'Pergunta': question,
                };
                
                Object.entries(responses).forEach(([response, count]) => {
                    row[response] = count;
                });
                
                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
            XLSX.utils.book_append_sheet(workbook, worksheet, `Feedback ${anoSelecionado}`);
        }

        // Gera o arquivo Excel e faz o download
        XLSX.writeFile(workbook, `relatorio_feedback_${modoComparacao ? 'comparacao' : anoSelecionado}.xlsx`);
    };

    const handleAbrirModalComparacao = () => {
        setAnosSelecionadosComparacao([...anosComparacao]);
        setShowModalComparacao(true);
    };

    const handleAplicarComparacao = () => {
        if (anosSelecionadosComparacao.length < 2) {
            alert('Selecione pelo menos 2 anos para comparar');
            return;
        }
        setAnosComparacao([...anosSelecionadosComparacao]);
        setModoComparacao(true);
        setShowModalComparacao(false);
    };

    const handleCancelarComparacao = () => {
        setModoComparacao(false);
        setAnosComparacao([]);
    };

    const handleToggleAnoComparacao = (ano) => {
        setAnosSelecionadosComparacao(prev => {
            if (prev.includes(ano)) {
                return prev.filter(a => a !== ano);
            } else {
                return [...prev, ano];
            }
        });
    };

    if (loading) {
        return <FeedbackSkeleton cardCount={4} />;
    }

    const renderGraficos = () => {
        if (!data) return null;

        if (modoComparacao && typeof data === 'object' && !Array.isArray(data)) {
            // Modo comparação: renderizar gráficos lado a lado por ano
            const perguntas = Object.keys(data[Object.keys(data)[0]] || {});
            
            return perguntas.map((question, questionIndex) => (
                <div key={questionIndex} className="col-12 mb-4">
                    <h5 className="mb-3">{question}</h5>
                    <div className="row g-3">
                        {Object.entries(data).map(([ano, estatisticasAno]) => {
                            const responses = estatisticasAno[question] || {};
                            return (
                                <div key={ano} className="col-md-6">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h6 className="card-title mb-3">Ano {ano}</h6>
                                            {renderVisualization(question, responses, `${questionIndex}-${ano}`)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ));
        } else {
            // Modo normal: um gráfico por pergunta
            return Object.entries(data).map(([question, responses], index) => (
                <div key={index} className="col-md-6">
                    <div className="card h-100">
                        <div className="card-body">
                            <h5 className="card-title mb-4">{question}</h5>
                            {renderVisualization(question, responses, index)}
                        </div>
                    </div>
                </div>
            ));
        }
    };

    return (
        <div className="fade-in w-100 mt-3" style={{ marginBottom: '100px' }}>
            <div className="rounded-3 mb-4">
                <div className="mb-3">
                    <h1 className="text-primary fs-2 fw-bold mb-2">Estatísticas de Feedback</h1>
                    <p className="text-muted mb-0">
                        Visualize as estatísticas das pesquisas de satisfação dos usuários.
                    </p>
                </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body p-3">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label text-primary fw-medium small mb-1">Ano</label>
                            <Select
                                value={anosOptions.find(opt => opt.value === anoSelecionado)}
                                onChange={(selected) => setAnoSelecionado(selected ? selected.value : new Date().getFullYear())}
                                options={anosOptions}
                                styles={customStyles}
                                placeholder="Selecione o ano"
                                isClearable={false}
                                isDisabled={modoComparacao}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                        {!modoComparacao && (
                            <div className="col-md-4">
                                <button 
                                    className="btn btn-outline-primary py-2"
                                    onClick={handleAbrirModalComparacao}
                                    disabled={anosDisponiveis.length < 2}
                                >
                                    <i className="bi bi-graph-up-arrow me-1"></i>
                                    Comparar Anos
                                </button>
                            </div>
                        )}
                        {modoComparacao && (
                            <div className="col-md-4">
                                <button 
                                    className="btn btn-outline-secondary w-100 py-2"
                                    onClick={handleCancelarComparacao}
                                >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Cancelar Comparação
                                </button>
                            </div>
                        )}
                        <div className="col-md-4 d-flex gap-2 justify-content-end">
                            <div className="btn-group">
                                <button 
                                    className={`btn ${viewType === 'pizza' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewType('pizza')}
                                >
                                    <i className="bi bi-pie-chart"></i> Pizza
                                </button>
                                <button 
                                    className={`btn ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewType('table')}
                                >
                                    <i className="bi bi-table"></i> Tabela
                                </button>
                                <button 
                                    className={`btn ${viewType === 'bar' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewType('bar')}
                                >
                                    <i className="bi bi-bar-chart"></i> Colunas
                                </button>
                            </div>
                            <button 
                                className="btn btn-success"
                                onClick={handleExportExcel}
                                disabled={!data}
                            >
                                <i className="bi bi-file-earmark-spreadsheet"></i> Download
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {modoComparacao && anosComparacao.length > 0 && (
                <div className="mb-3 d-flex flex-wrap gap-2">
                    <span className="badge bg-primary d-flex align-items-center gap-1" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                        Comparando: {anosComparacao.join(', ')}
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            style={{ fontSize: '0.6rem' }}
                            onClick={handleCancelarComparacao}
                            aria-label="Remover comparação"
                        ></button>
                    </span>
                </div>
            )}

            <div className="row g-4">
                {renderGraficos()}
            </div>

            <Modal show={showModalComparacao} onHide={() => setShowModalComparacao(false)} centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Comparar Anos</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <p>Selecione pelo menos 2 anos para comparar:</p>
                    {anosDisponiveis.map(ano => (
                        <div key={ano} className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={anosSelecionadosComparacao.includes(ano)}
                                onChange={() => handleToggleAnoComparacao(ano)}
                                id={`ano-${ano}`}
                            />
                            <label className="form-check-label" htmlFor={`ano-${ano}`}>
                                {ano}
                            </label>
                        </div>
                    ))}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={() => setShowModalComparacao(false)}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleAplicarComparacao}>
                            Aplicar Comparação
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default RelatorioFeedback;